// Zod schemas — shared by api (request validation) and web (form validation).
import { z } from 'zod';
import { AGENT_STATUSES, RISK_LEVELS } from './enums.js';
export const LoginInputSchema = z.object({
    username: z.string().min(1, 'Username is required').max(64),
    password: z.string().min(1, 'Password is required').max(128),
});
// ---------- Agents ----------
const BlueprintSkillSchema = z.object({
    name: z.string().min(1).max(120),
    risk: z.enum(RISK_LEVELS),
});
export const AgentBlueprintSchema = z.object({
    name: z.string().min(1, 'Agent name is required').max(120),
    description: z.string().min(1, 'Description is required').max(4000),
    agentType: z.string().min(1).max(80),
    languages: z.array(z.string().min(1).max(40)).min(1).max(10),
    tone: z.string().min(1).max(120),
    primaryChannel: z.string().min(1).max(40),
    skills: z.array(BlueprintSkillSchema).max(40),
    requiredMcps: z.array(z.string().min(1).max(80)).max(40),
    guardrails: z.array(z.string().min(1).max(280)).max(40),
});
export const GenerateBlueprintInputSchema = z.object({
    description: z.string().min(10, 'Tell me a bit more about the agent').max(4000),
    industry: z.string().max(80).optional(),
    primaryChannel: z.string().max(40).optional(),
    languages: z.array(z.string().min(1).max(40)).max(10).optional(),
});
export const CreateAgentInputSchema = z.object({
    blueprint: AgentBlueprintSchema,
});
export const UpdateAgentInputSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().min(1).max(4000).optional(),
    defaultLanguage: z.string().min(1).max(40).optional(),
    tone: z.string().min(1).max(120).optional(),
    status: z.enum(AGENT_STATUSES).optional(),
});
export const UpdateAgentSkillsInputSchema = z.object({
    enabledSkillIds: z.array(z.string().min(1)).max(80),
});
// ---------- MCP ----------
import { MCP_AUTH_TYPES, MCP_CATEGORIES, TOOL_PERMISSIONS } from './enums.js';
export const CreateMcpConnectionInputSchema = z.object({
    name: z.string().min(1, 'Name is required').max(120),
    category: z.enum(MCP_CATEGORIES),
    description: z.string().max(500).optional(),
    endpointUrl: z
        .string()
        .min(1, 'Endpoint is required')
        .max(500)
        // Permissive: prototype/dev MCPs may use mock:// or other schemes.
        .refine((s) => /^[a-z][a-z0-9+\-.]*:\/\//i.test(s), 'Endpoint must be a URL'),
    authType: z.enum(MCP_AUTH_TYPES),
    authConfig: z.record(z.string(), z.string()).optional(),
});
export const UpdateMcpConnectionInputSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    category: z.enum(MCP_CATEGORIES).optional(),
    description: z.string().max(500).nullable().optional(),
    endpointUrl: z.string().min(1).max(500).optional(),
    authType: z.enum(MCP_AUTH_TYPES).optional(),
    authConfig: z.record(z.string(), z.string()).optional(),
});
export const UpdateMcpPermissionsInputSchema = z.object({
    changes: z
        .array(z.object({
        agentId: z.string().min(1),
        toolId: z.string().min(1),
        permission: z.enum(TOOL_PERMISSIONS),
        requiresApproval: z.boolean(),
    }))
        .max(2000),
});
export const CallToolInputSchema = z.object({
    input: z.record(z.string(), z.unknown()).optional(),
});
// ---------- Channels ----------
import { CHANNEL_PROVIDERS, CHANNEL_STATUSES_INPUT, CHANNEL_TYPES } from './enums.js';
export const CreateChannelInputSchema = z.object({
    type: z.enum(CHANNEL_TYPES),
    provider: z.enum(CHANNEL_PROVIDERS),
    displayName: z.string().min(1).max(120),
    externalIdentifier: z.string().max(200).optional(),
});
export const UpdateChannelInputSchema = z.object({
    displayName: z.string().min(1).max(120).optional(),
    externalIdentifier: z.string().max(200).nullable().optional(),
    status: z.enum(CHANNEL_STATUSES_INPUT).optional(),
});
export const BindAgentInputSchema = z.object({
    agentId: z.string().min(1),
    enabled: z.boolean().optional(),
});
export const CreateWhatsAppSessionInputSchema = z.object({
    phoneNumber: z
        .string()
        .min(6)
        .max(32)
        .regex(/^\+?[0-9 ()-]+$/, 'Phone number must be digits with optional + and separators'),
    displayName: z.string().max(120).optional(),
});
// ---------- Conversations ----------
export const SendMessageInputSchema = z.object({
    text: z.string().min(1).max(4000),
});
export const SetBotEnabledInputSchema = z.object({
    enabled: z.boolean(),
});
// ---------- Workflows ----------
import { WORKFLOW_STATUSES } from './enums.js';
export const WorkflowStepSchema = z.object({
    id: z.string().min(1).max(120),
    name: z.string().min(1).max(120),
    tool: z.string().min(1).max(160),
    description: z.string().max(2000),
    inputs: z.record(z.string(), z.unknown()),
    outputs: z.array(z.string().min(1).max(120)).max(40).optional(),
    timeoutMs: z.number().int().positive().optional(),
    retry: z
        .object({
        attempts: z.number().int().min(0).max(10),
        backoffMs: z.number().int().min(0).max(60000),
    })
        .optional(),
    onFailure: z.enum(['fail', 'skip', 'route_to_human']).optional(),
});
export const WorkflowDefinitionSchema = z.object({
    steps: z.array(WorkflowStepSchema).max(40),
});
export const CreateWorkflowInputSchema = z.object({
    name: z.string().min(1, 'Name is required').max(120),
    description: z.string().max(1000).optional(),
    triggerType: z.string().min(1, 'Trigger type is required').max(120),
    status: z.enum(WORKFLOW_STATUSES).optional(),
    definition: WorkflowDefinitionSchema.optional(),
});
export const UpdateWorkflowInputSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(1000).nullable().optional(),
    triggerType: z.string().min(1).max(120).optional(),
    status: z.enum(WORKFLOW_STATUSES).optional(),
    definition: WorkflowDefinitionSchema.nullable().optional(),
});
//# sourceMappingURL=schemas.js.map